import {useEffect, useState} from "react"
import {CircularProgress, IconButton, Stack, Typography} from "@mui/joy"
import {ArrowLeft, Eraser} from "lucide-react"
import {useTranslation} from "react-i18next"
import Card from "../components/Card"
import Button from "../components/Button"
import PageContainer from "../components/layout/PageContainer"
import CompetitionSearchField from "../components/CompetitionSearchField"
import FeaturedCompetitions from "../components/FeaturedCompetitions"
import LeagueFixtures from "../components/league/LeagueFixtures"
import MatchAnalysis from "../components/MatchAnalysis"
import AnalyzeProgress from "../components/AnalyzeProgress"
import SubscriptionNotice from "../components/SubscriptionNotice"
import {useAnalyze} from "../hooks/useAnalyze"
import {useLeagueFixtures} from "../hooks/useLeagueFixtures"
import {FixtureResponse} from "../types/fixture"
import {LeagueResponse} from "../types/league"

/**
 * Matches reached by the competition they are played in.
 *
 * The analysis page starts from the two clubs, which only works for someone who already has a
 * fixture in mind. This is the other way in: pick a competition, read what it has coming up, and
 * analyse a match straight from the list.
 */
const Competitions = () => {
    const {t} = useTranslation()
    const analyze = useAnalyze()

    const [league, setLeague] = useState<LeagueResponse>()
    const [searchKey, setSearchKey] = useState(0)
    /** The fixture being analysed, so its row reads as picked */
    const [analysed, setAnalysed] = useState<FixtureResponse>()

    const leagueFixtures = useLeagueFixtures(league?.league?.id)

    // A competition of its own has nothing to do with the analysis the previous one produced
    useEffect(() => {
        analyze.reset()
        setAnalysed(undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [league?.league?.id])

    /**
     * The clubs are handed over with their ids, which is what pairs them with the prediction
     * provider: the two systems spell clubs differently, and an id leaves nothing to guess.
     */
    const analyzeFixture = (fixture: FixtureResponse) => {
        setAnalysed(fixture)

        return analyze.analyze(
            {name: fixture.teams?.home?.name, teamId: fixture.teams?.home?.id},
            {name: fixture.teams?.away?.name, teamId: fixture.teams?.away?.id}
        )
    }

    const clear = () => {
        setLeague(undefined)
        setSearchKey(key => key + 1)
        setAnalysed(undefined)
        analyze.reset()
    }

    const analysisShown = !!analyze.event

    return <PageContainer width={{
        md: analyze.event ? "48rem" : "38rem",
        lg: analyze.event ? "68rem" : "52rem"
    }}>

        {analysisShown
            ? <Stack direction={"row"} alignItems={"center"} gap={1}>
                <IconButton variant={"outlined"} color={"neutral"} onClick={analyze.reset}
                            aria-label={t("backToCompetition")}>
                    <ArrowLeft size={18}/>
                </IconButton>
                <Typography level={"title-md"}>{t("backToCompetition")}</Typography>
            </Stack>
            : <Stack alignItems={"center"} gap={1}>
                <Typography level={"h2"} textAlign={"center"} fontSize={{xs: "1.6rem", sm: undefined}}>
                    {t("competitionsTitle")}
                </Typography>
                <Typography textAlign={"center"}>{t("competitionsSubtitle")}</Typography>
            </Stack>}

        {!analysisShown && <Card>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                <Typography>{t("competitionToBrowse")}</Typography>

                {!!league && <Button size={"sm"} variant={"soft"} color={"neutral"}
                                     startDecorator={<Eraser size={16}/>}
                                     onClick={clear}
                                     sx={{background: "rgba(255,255,255,0.06)", color: "text.tertiary"}}>
                    {t("clearSearch")}
                </Button>}
            </Stack>

            <CompetitionSearchField key={`competition-${searchKey}`}
                                    placeholder={t("competitionSearchPlaceholder")}
                                    selected={league}
                                    onSelect={setLeague}/>

            {/* What most visits are after, offered as something to tap rather than to spell */}
            <FeaturedCompetitions selected={league} onSelect={setLeague}/>

            {analyze.loading && <AnalyzeProgress progress={analyze.progress} onCancel={analyze.cancel}/>}

            {analyze.error && <Typography level={"body-xs"} color={"danger"}>{analyze.error}</Typography>}

            {/* A refusal is not an error to retry: it is a plan to buy, and it says which one */}
            {analyze.denial && <SubscriptionNotice denial={analyze.denial}/>}
        </Card>}

        {analyze.event && <MatchAnalysis event={analyze.event}
                                         homeLogo={analysed?.teams?.home?.logo}
                                         awayLogo={analysed?.teams?.away?.logo}/>}

        <Card>
            <Typography>
                {league
                    ? t("upcomingMatchesIn", {competition: league.league?.name})
                    : t("upcomingMatches")}
            </Typography>

            {leagueFixtures.loading
                ? <Typography startDecorator={<CircularProgress size={"sm"}/>}>{t("searchingUpcoming")}</Typography>
                : !league
                    ? <Typography level={"body-sm"}>{t("pickCompetitionHint")}</Typography>
                    : leagueFixtures.failed
                        ? <Typography level={"body-sm"} color={"danger"}>{t("competitionSearchUnavailable")}</Typography>
                        : leagueFixtures.fixtures.length
                            ? <LeagueFixtures fixtures={leagueFixtures.fixtures}
                                              selectedId={analysed?.fixture?.id}
                                              onSelect={analyzeFixture}/>
                            : <Typography level={"body-sm"}>{t("noUpcomingMatchInCompetition")}</Typography>}
        </Card>

    </PageContainer>
}

export default Competitions
